import { useState } from "react";
import {
  ActionIcon,
  Group,
  Popover,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import IconChevronRight from "@tabler/icons-react/dist/esm/icons/IconChevronRight.mjs";
import IconSettings from "@tabler/icons-react/dist/esm/icons/IconSettings.mjs";

import {
  useAddConditionFunctionMutation,
  useDeleteEntityFieldFunctionMutation,
  useUpdateFieldMutation,
} from "../../../hooks/mutations";
import { useFunctionsQuery } from "../../../hooks/useFunctionsQuery";
import { buildFieldPutData } from "../../../utils/conditionField";
import { DeleteConfirmModal } from "../../../../DeleteConfirmationModal";
import classes from "./MoreActions.module.scss";

/**
 * Popover menu attached to each condition field. Provides:
 *  - Type switching (FIELD / VALUE / FORMULA / DATE) — fires updateField
 *  - "Functions" submenu — addConditionFunction (or delete when a parent
 *    function is the current context)
 *  - Change alias → flips the parent into name-edit mode
 *
 * parentFunctionId: when set, the popover shows function-related actions
 * instead of the type-switch menu (used by FieldFunctionWrapper).
 */
export default function MoreActions({
  ruleId,
  fieldsData,
  setNameEditMode,
  parentFunctionId,
  showFunctionsPopover,
  showChangeAlias,
  showTypeSwitch = true,
  id,
  fieldType,
  isRightField,
  allowValueEntry,
  allowDateEntry,
}) {
  const [opened, setOpened] = useState(false);
  const [functionsOpened, setFunctionsOpened] = useState(false);
  const [deleteOpened, setDeleteOpened] = useState(false);

  const { data: functionsData } = useFunctionsQuery();
  const { mutate: updateField } = useUpdateFieldMutation({ ruleId });
  const { mutate: addConditionFunction } = useAddConditionFunctionMutation({
    ruleId,
  });
  const { mutate: deleteEntityFieldFunction } =
    useDeleteEntityFieldFunctionMutation({ ruleId });

  const close = () => {
    setOpened(false);
    setFunctionsOpened(false);
  };

  const changeFieldType = (type) => {
    const putData = { ...buildFieldPutData(fieldsData) };
    if (isRightField) {
      putData.type = type;
    } else {
      putData.leftType = type;
    }
    updateField(putData);
    close();
  };

  const handleAddFunction = (functionId) => {
    addConditionFunction({
      functionId,
      parentEntityFieldFunctionId: parentFunctionId || null,
      entityFieldId: id,
    });
    close();
  };

  const handleDeleteFunction = () => {
    deleteEntityFieldFunction({ id: parentFunctionId });
    setDeleteOpened(false);
  };

  // When attached to a FieldFunctionWrapper, the popover is just function
  // actions (add child function / delete this function).
  const functionMenuItems = (
    <Stack gap={0}>
      {functionsData?.map((fn) => (
        <UnstyledButton
          key={fn.id}
          className={classes.menuItem}
          onClick={() => handleAddFunction(fn.id)}
        >
          {fn.name}
        </UnstyledButton>
      ))}
      {parentFunctionId && (
        <UnstyledButton
          className={classes.menuItem}
          onClick={() => setDeleteOpened(true)}
        >
          Delete
        </UnstyledButton>
      )}
    </Stack>
  );

  const typeMenuItems = (
    <Stack gap={0}>
      {showFunctionsPopover && (
        <UnstyledButton
          className={classes.menuItem}
          onMouseEnter={() => setFunctionsOpened(true)}
        >
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Text size="sm">Functions</Text>
            <IconChevronRight size={14} />
          </Group>
        </UnstyledButton>
      )}
      {showTypeSwitch && (
        <>
          {fieldType !== "FIELD" && (
            <UnstyledButton
              className={classes.menuItem}
              onClick={() => changeFieldType("FIELD")}
            >
              Entity
            </UnstyledButton>
          )}
          {allowValueEntry && fieldType !== "VALUE" && (
            <UnstyledButton
              className={classes.menuItem}
              onClick={() => changeFieldType("VALUE")}
            >
              Value
            </UnstyledButton>
          )}
          {fieldType !== "FORMULA" && (
            <UnstyledButton
              className={classes.menuItem}
              onClick={() => changeFieldType("FORMULA")}
            >
              Formula
            </UnstyledButton>
          )}
          {fieldType !== "DATE" && allowDateEntry && (
            <>
              <UnstyledButton
                className={classes.menuItem}
                onClick={() => changeFieldType("DATE")}
              >
                Date
              </UnstyledButton>
              <UnstyledButton
                className={classes.menuItem}
                onClick={() => changeFieldType("VALUE")}
              >
                Value
              </UnstyledButton>
            </>
          )}
        </>
      )}
      {showChangeAlias && (
        <UnstyledButton
          className={classes.menuItemLast}
          onMouseEnter={() => setFunctionsOpened(false)}
          onClick={() => {
            setNameEditMode?.(true);
            close();
          }}
        >
          Change alias
        </UnstyledButton>
      )}
    </Stack>
  );

  return (
    <>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        withinPortal
        shadow="md"
      >
        <Popover.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Field options"
            onClick={() => setOpened((v) => !v)}
          >
            <IconSettings size={16} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown p={0}>
          <div className={classes.menuWrap}>
            <div className={classes.menuColumn}>
              {parentFunctionId ? functionMenuItems : typeMenuItems}
            </div>
            {functionsOpened && !parentFunctionId && showFunctionsPopover && (
              <div className={classes.subMenuColumn}>{functionMenuItems}</div>
            )}
          </div>
        </Popover.Dropdown>
      </Popover>

      <DeleteConfirmModal
        opened={deleteOpened}
        onClose={() => setDeleteOpened(false)}
        onDelete={handleDeleteFunction}
        label="Delete function?"
      />
    </>
  );
}
